import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Typography,
  Box,
  Stack,
  Tabs,
  Tab,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { HexColorPicker } from "react-colorful";
import ZoomableImage from "./ZoomableImage";

interface ColorPickerProps {
  onImageUpload: (file: File) => void;
  onColorExtracted?: (color: string) => void;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// Custom styled wrapper for the color picker
const StyledColorPicker = styled(HexColorPicker)({
  width: "100% !important",
  height: "auto !important",
  "& .react-colorful__saturation": {
    width: "100% !important",
    height: "145px !important",
    borderRadius: "4px 4px 0 0 !important",
  },
  "& .react-colorful__hue": {
    width: "100% !important",
    height: "30px !important",
    borderRadius: "0 0 4px 4px !important",
  },
});

export const ColorPicker = ({
  onImageUpload,
  onColorExtracted,
}: ColorPickerProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string>("#1976d2");
  const [tabValue, setTabValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pasteInputRef = useRef<HTMLInputElement | null>(null);

  // Focus the paste input on component mount since image tab is first by default
  useEffect(() => {
    setTimeout(() => {
      if (tabValue === 0 && pasteInputRef.current) {
        pasteInputRef.current.focus();
      }
    }, 100);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setTimeout(() => {
        pasteInputRef.current?.focus();
      }, 0);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const processFile = useCallback(
    (file: File) => {
      // Create a preview URL for the uploaded image
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);

      // Call the callback with the file
      onImageUpload(file);
    },
    [onImageUpload]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null); // Clear any previous errors

    // First try to get files directly
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.match(/image.*/)) {
        processFile(file);
        return;
      }
    }

    // If no direct files, check for image URLs from browser
    try {
      // Check for HTML content (which is how browser images are usually dragged)
      const htmlContent = e.dataTransfer.getData("text/html");
      if (htmlContent) {
        // Create a temporary DOM element to parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        const imgElement = doc.querySelector("img");

        if (imgElement && imgElement.src) {
          await fetchAndProcessImageFromUrl(imgElement.src);
          return;
        }
      }

      // Check for plain URL
      const urlContent =
        e.dataTransfer.getData("text/uri-list") ||
        e.dataTransfer.getData("text/plain");
      if (urlContent) {
        // Check if it's an image URL by extension or by trying to fetch
        if (urlContent.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i)) {
          await fetchAndProcessImageFromUrl(urlContent);
          return;
        }

        // Try to detect image content type for URLs without extensions
        try {
          const response = await fetch(urlContent, {
            method: "HEAD",
            mode: "cors",
          });

          const contentType = response.headers.get("content-type");
          if (contentType && contentType.startsWith("image/")) {
            await fetchAndProcessImageFromUrl(urlContent);
            return;
          }
        } catch (error) {
          console.error("Error checking URL content type:", error);
        }
      }

      // If we've reached here, no valid image was found
      setError("The dragged content is not a valid image");
    } catch (error) {
      console.error("Error processing dragged image:", error);
      setError(
        "Failed to process the dragged image. Please try uploading directly."
      );
    }
  };

  // Helper function to fetch an image from URL and process it
  const fetchAndProcessImageFromUrl = async (url: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Use crossorigin.me to handle CORS issues
      const proxyUrl = "https://corsproxy.io/?key=9e768559&url=" + url;

      // Create a fetch request through the proxy
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch image: ${response.status} ${response.statusText}`
        );
      }

      // Convert the response to a blob
      const blob = await response.blob();

      // Only process valid image types
      if (!blob.type.match(/image\/(jpeg|jpg|png|gif|webp|svg\+xml)/i)) {
        throw new Error("The dragged content is not a valid image");
      }

      // Convert the blob to a file
      const filename = url.split("/").pop() || "dragged-image.jpg";
      const file = new File([blob], filename, {
        type: blob.type || "image/jpeg",
      });

      // Process the file
      processFile(file);
    } catch (error) {
      console.error("Error fetching image from URL:", error);
      setError(
        "Failed to load the image. Please try downloading it first and then uploading it."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Add useEffect to automatically take a screenshot when tab 2 is selected
  useEffect(() => {
    if (tabValue === 2) {
      takeScreenshot();
    }
  }, [tabValue]);

  // Function to clear the image
  const clearImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setSelectedColor("#1976d2");

    // Reset the file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    pasteInputRef.current?.focus();
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Clean up both previewUrl and screenshotUrl to prevent memory leaks
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (screenshotUrl && screenshotUrl.startsWith("blob:")) {
        URL.revokeObjectURL(screenshotUrl);
      }
    };
  }, [previewUrl, screenshotUrl]);

  // Handle color change from the color picker to avoid unnecessary re-renders
  const handleColorChange = useCallback(
    (color: string) => {
      setSelectedColor(color);
      if (onColorExtracted) {
        onColorExtracted(color);
      }
    },
    [onColorExtracted]
  );

  // Function to load an image from URL
  const handleImageUrlLoad = async () => {
    if (!imageUrl.trim()) {
      setError("Please enter a valid image URL");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a new Image element to load the image
      const img = new Image();

      // Create a promise to handle the image loading
      const imageLoadPromise = new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to load image from URL"));

        // Set crossOrigin to anonymous to handle CORS issues
        img.crossOrigin = "anonymous";
        img.src = imageUrl;
      });

      // Wait for the image to load
      await imageLoadPromise;

      // Convert the image to a Blob
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a File object from the Blob
            const file = new File([blob], "image-from-url.png", {
              type: "image/png",
            });

            // Process the file just like we do with uploaded files
            processFile(file);
          } else {
            throw new Error("Failed to convert image to file");
          }
        }, "image/png");
      }
    } catch (err) {
      console.error("Error loading image from URL:", err);

      // Errormessages based on error type
      if (err instanceof TypeError && err.message.includes("NetworkError")) {
        setError(
          "Network error: The image might be protected or the server doesn't allow cross-origin access."
        );
      } else if (imageUrl.includes("file:")) {
        setError(
          "Security error: Can't load local files. Please use a web URL."
        );
      } else if (!imageUrl.match(/^https?:\/\//)) {
        setError(
          "Invalid URL: Make sure the URL starts with http:// or https://"
        );
      } else {
        setError("Failed to load image. Please check the URL and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to take a screenshot of the Three.js canvas
  const takeScreenshot = () => {
    // Check if the Three.js renderer is available
    if (!window.gl) {
      console.error("Three.js renderer not found!");
      return;
    }

    try {
      // First render the scene immediately before taking the screenshot
      if (window.scene && window.camera) {
        window.gl.render(window.scene, window.camera);
      }

      // Capture the canvas content right after rendering
      const dataUrl = window.gl.domElement.toDataURL("image/jpeg", 0.8);

      // Set the screenshot URL to state
      setScreenshotUrl(dataUrl);
    } catch (error) {
      console.error("Error taking screenshot:", error);
      // Add user feedback here
      setError("Failed to take screenshot. Please try again.");
    }
  };

  // Function to clear the screenshot
  const clearScreenshot = () => {
    setScreenshotUrl(null);
  };

  // Consolidated paste handling using a single function
  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (tabValue !== 0) return;

      // Check if the active element is an input field we want to exclude
      const activeElement = document.activeElement;
      const isTextInput =
        activeElement instanceof HTMLInputElement &&
        activeElement.type === "text" &&
        activeElement !== pasteInputRef.current;

      if (isTextInput) {
        // Don't process paste if focused on text inputs (except our hidden paste input)
        return;
      }

      const items = e.clipboardData?.items;
      if (items) {
        let foundImage = false;
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf("image") !== -1) {
            const file = items[i].getAsFile();
            if (file) {
              processFile(file);
              e.preventDefault(); // Prevent default paste behavior
              foundImage = true;
              break; // Process only first image
            }
          }
        }

        if (foundImage && activeElement !== pasteInputRef.current) {
          // Refocus our paste input after processing
          pasteInputRef.current?.focus();
        }
      }
    },
    [tabValue, processFile]
  );

  useEffect(() => {
    // Add the consolidated paste handler
    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [handlePaste]);

  return (
    <Stack spacing={2}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: "divider",

          "& .MuiTabs-indicator": {
            backgroundColor: (theme) => theme.palette.primary.shades[200],
            height: "2.5px",
          },
        }}
      >
        <Tab label="From Image" />
        <Tab label="Color Graph" />
        <Tab label="From Board" />
      </Tabs>

      {tabValue === 2 && (
        <Box
          ref={containerRef}
          sx={{
            padding: "0px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "185px",
            border: "1px dashed grey",
            borderRadius: "8px",
            gap: 2,
          }}
        >
          {screenshotUrl ? (
            <>
              <Box sx={{ width: "100%", position: "relative" }}>
                <IconButton
                  aria-label="remove screenshot"
                  onClick={clearScreenshot}
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    padding: "4px",
                    margin: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.9)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Use our extracted ZoomableImage component */}
                <ZoomableImage
                  imgSrc={screenshotUrl}
                  onColorPick={(color: string) => {
                    setSelectedColor(color);
                    if (onColorExtracted) {
                      onColorExtracted(color);
                    }
                  }}
                  zoomLevel={4}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={takeScreenshot}
                sx={{ fontSize: "14px", padding: "8px 8px" }}
              >
                Take a board screenshot
              </Button>
            </Box>
          )}
        </Box>
      )}

      {tabValue === 0 && (
        <>
          {/* <Typography
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              marginTop: "0px",
            }}
          >
            Upload an image and click directly on it to pick a color
          </Typography> */}

          <Box
            ref={containerRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              pasteInputRef.current?.focus();
            }}
            sx={{
              position: "relative",
              cursor: previewUrl ? "crosshair" : "default",
              border: isDragging
                ? "2px dashed #1976d2"
                : isFocused
                ? "2px dashed #1976d2"
                : "1px dashed grey",
              borderRadius: "8px",
              padding: "0px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50px",
              transition: "all 0.2s ease",
              outline: "none",
              background:
                isDragging || isFocused
                  ? "rgba(25, 118, 210, 0.05)"
                  : "transparent",
            }}
          >
            {/* Hidden input to capture paste events */}
            <input
              ref={pasteInputRef}
              style={{
                position: "absolute",
                opacity: 0,
                height: 0,
                width: "100%",
                top: 0,
                left: 0,
                pointerEvents: "none",
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />

            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />

            {previewUrl ? (
              <>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                  }}
                  onClick={(e) => {
                    // Prevent click event from taking focus away from paste input
                    e.stopPropagation();

                    pasteInputRef.current?.focus();
                  }}
                >
                  <IconButton
                    aria-label="remove image"
                    onClick={clearImage}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 10,
                      backgroundColor: "rgba(255,255,255,0.7)",
                      padding: "2px",
                      margin: "2px",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {/* Use our extracted ZoomableImage component */}
                  <ZoomableImage
                    imgSrc={previewUrl}
                    onColorPick={(color: string) => {
                      setSelectedColor(color);
                      if (onColorExtracted) {
                        onColorExtracted(color);
                      }
                    }}
                    zoomLevel={4}
                  />
                </Box>
              </>
            ) : (
              <Stack
                alignItems="center"
                spacing={1}
                sx={{ width: "100%", padding: "15px 0" }}
              >
                <CloudUploadIcon
                  sx={{ fontSize: 40, color: "text.secondary" }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Drag & drop or{" "}
                  <span
                    style={{ color: "blue", cursor: "pointer" }}
                    onClick={handleUploadClick}
                  >
                    upload image
                  </span>
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  Copy & paste from clipboard using Ctrl+V or Cmd+V
                </Typography>

                {isLoading && (
                  <Typography variant="body2" color="text.secondary">
                    Loading dragged image...
                  </Typography>
                )}

                {error && !isLoading && (
                  <Typography
                    variant="body2"
                    color="error"
                    align="center"
                    sx={{ maxWidth: "90%" }}
                  >
                    {error}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
          {/* <Typography
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              marginTop: "0px",
            }}
          >
            Or paste image URL here
          </Typography> */}
          <Box
            sx={{
              width: "100%",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              placeholder="Paste image URL here"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                setError(null);
              }}
              disabled={isLoading}
              error={!!error}
              helperText={error}
              size="small"
              variant="outlined"
            />
            <Box
              component="span"
              onClick={handleImageUrlLoad}
              sx={{
                color: imageUrl.trim() && !isLoading ? "blue" : "text.disabled",
                cursor: imageUrl.trim() && !isLoading ? "pointer" : "default",
                fontWeight: "medium",
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                userSelect: "none",
                transition: "color 0.2s",
                "&:hover": {
                  textDecoration:
                    imageUrl.trim() && !isLoading ? "underline" : "none",
                },
              }}
            >
              {isLoading ? "Loading..." : "Load Image"}
            </Box>
          </Box>
        </>
      )}

      {tabValue === 1 && (
        <>
          {/* <Typography
            sx={{
              color: "text.secondary",
              fontSize: "14px",
              alignSelf: "flex-start",
            }}
          >
            Select a color from the color picker
          </Typography> */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed grey",
              borderRadius: "8px",
              maxWidth: "100%",
            }}
          >
            <StyledColorPicker
              color={selectedColor}
              onChange={handleColorChange}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ColorPicker;
